<template>
  <div>
    <custom-popup
      v-model="walletModal"
      :cons="{ title: $t('newUserTransactions.userWallet') }"
      hide-confirm
      :key="$i18n.locale + '_popup'"
    >
      <v-card :loading="priceLoading">
        <v-chip :loading="priceLoading" class="ma-2" color="primary" label>
          {{ $t("newUserTransactions.terminal") }}:
          {{
            use_lang(!!userBalances.core ? price(userBalances.core) : price(0))
          }}
          {{ $t("newUserTransactions.rial") }}
        </v-chip>

        <v-chip
          v-can="'admin_operator_wallet'"
          class="ma-2"
          color="primary"
          label
        >
          {{ $t("newUserTransactions.liveSms") }}:
          {{
            use_lang(
              !!userBalances.payamak ? price(userBalances.payamak) : price(0)
            )
          }}
          {{ $t("newUserTransactions.rial") }}
        </v-chip>

        <v-chip
          v-can="'admin_real_estate_wallet'"
          class="ma-2"
          color="primary"
          label
        >
          {{ $t("newUserTransactions.realEstateNews") }}:
          {{
            use_lang(
              !!userBalances.statePrice
                ? price(userBalances.statePrice)
                : price(0)
            )
          }}
          {{ $t("newUserTransactions.rial") }}
        </v-chip>
      </v-card>
    </custom-popup>

    <custom-popup
      v-model="transactionListModal"
      :cons="{ title: $t('newUserTransactions.transactionList') }"
      hide-confirm
    >
      <v-card :loading="loading" flat>
        <v-card-title>
          <v-spacer />
          {{ $t("newUserTransactions.userTransactions") }}
          <v-spacer />
        </v-card-title>

        <v-divider />

        <v-card-text>
          <vue-table
            :data="transactions"
            :headers="headers"
            :loading.sync="loading"
            @change="onPagination"
          >
            <template v-slot:filters="{ props, perPage }">
              <v-row no-gutters>
                <v-col class="pa-2" cols="12" md="2">
                  <v-select
                    v-model="props.length"
                    :items="perPage"
                    class="rounded-lg"
                    dense
                    hide-details
                    :label="$t('newUserTransactions.perPage')"
                    outlined
                  />
                </v-col>
                <v-col class="pa-2" cols="12" lg="4" md="6">
                  <v-text-field
                    v-model="props.search"
                    append-icon="mdi-magnify"
                    class="rounded-lg"
                    clearable
                    dense
                    hide-details
                    :label="$t('newUserTransactions.search')"
                    outlined
                  />
                </v-col>
              </v-row>
            </template>

            <template v-slot:index="{ item }">
              {{
                use_lang(
                  item.index +
                    1 +
                    (transactions.current_page - 1) * transactions.per_page
                )
              }}
            </template>

            <template v-slot:type="{ item }">
              <v-chip
                v-if="!!item.type"
                :color="$options.filters.transactionType(item.type).color"
                label
                outlined
                small
              >
                {{ $t(`newUserTransactions.transactionTypes.${item.type}`) }}
              </v-chip>
            </template>
            <template v-slot:amount.$numberDecimal="{ item }">
              <v-chip
                :color="
                  item.type === 'withdraw' || item.state==='decrease' ? '#F64E60' : 'rgb(27,197,189)'
                "
                dark
                label
                small
              >
                {{ use_lang(price(item.amount.$numberDecimal)) }}
              </v-chip>
            </template>
            <template v-slot:balanceAfter.$numberDecimal="{ item }">
              {{ use_lang(price(item.balanceAfter.$numberDecimal)) }}
            </template>
            <template v-slot:serviceId="{ item }">
              <v-chip color="#6993ff" dark label small>
                {{ $options.filters.serviceTypeNumber(item.serviceId) }}
              </v-chip>
            </template>
            <template v-slot:createdAt="{ item }">
              <div class="text-nowrap">
                {{ use_lang($options.filters.onlyDate(item.createdAt)) }}
              </div>
            </template>
            <template v-slot:description="{ item }">
              <div class="link-wrapper" v-html="item.description" />
            </template>
            <template v-slot:time="{ item }">
              <div class="text-nowrap">
                {{ use_lang($options.filters.onlyTime(item.createdAt)) }}
              </div>
            </template>
            <template v-slot:product.description="{ item }">
              <v-chip
                v-if="!!item.product && !!item.product.description"
                color="#181c32"
                dark
                label
                small
              >
                {{ item.product.description }}
              </v-chip>
            </template>
          </vue-table>
        </v-card-text>
      </v-card>
    </custom-popup>

    <v-speed-dial v-model="fab" direction="right" transition="slide-x">
      <template v-slot:activator>
        <v-btn v-model="fab" icon>
          <v-icon v-if="fab"> mdi-close </v-icon>
          <v-icon v-else> mdi-dots-vertical </v-icon>
        </v-btn>
      </template>

      <v-tooltip v-if="can('children_balance')" top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ml-1 btn-update rounded-pill"
            fab
            small
            style="padding: 8px !important"
            v-bind="attrs"
            @click="walletShow(data)"
            v-on="on"
          >
            <v-icon dark>mdi-wallet-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("newUserTransactions.balance") }}</span>
      </v-tooltip>

      <v-tooltip v-if="can('children_transaction')" top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ml-1 btn-grp-operation rounded-pill"
            fab
            small
            style="padding: 8px !important"
            v-bind="attrs"
            @click="transactionShow"
            v-on="on"
          >
            <v-icon dark>mdi-list-box-outline</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("newUserTransactions.transactionListTooltip") }}</span>
      </v-tooltip>

      <v-tooltip v-if="can('children_panel')" top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ml-1 btn-management rounded-pill"
            fab
            href=""
            small
            style="padding: 8px !important"
            v-bind="attrs"
            @click="action(data, 'checkout')"
            v-on="on"
          >
            <v-icon dark>mdi-login</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("newUserTransactions.panelLogin") }}</span>
      </v-tooltip>

      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class="ml-1 btn-history rounded-pill"
            fab
            small
            style="padding: 8px !important"
            v-bind="attrs"
            @click="action(data, 'history')"
            v-on="on"
          >
            <v-icon dark>mdi-history</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("newUserTransactions.history") }}</span>
      </v-tooltip>
    </v-speed-dial>
  </div>
</template>

<script>
import { can } from "@/core/plugins/acl";

export default {
  props: {
    data: {},
    action: {
      type: Function,
    },
    meta: {
      type: Object,
    },
  },

  data() {
    return {
      USER_ROLE_LEVEL: process.env.VUE_APP_USER_ROLE_LEVEL,
      fab: false,
      userBalances: {
        core: 0,
        payamak: 0,
        statePrice: 0,
      },
      priceLoading: false,
      walletModal: false,
      headers: [
        {
          text: "",
          value: "index",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: "",
          value: "uniqueId",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: "",
          value: "amount.$numberDecimal",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: "",
          value: "time",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: "",
          value: "createdAt",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: "",
          value: "description",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: "",
          value: "type",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: "",
          value: "product.description",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: "",
          value: "serviceId",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: "",
          value: "balanceAfter.$numberDecimal",
          align: "center",
          class: "font-weight-bold h6",
        },
      ],
      tableProps: {
        search: "",
        length: 10,
        column: "id",
        dir: "desc",
        page: 1,
      },
      transactions: {},
      transactionListModal: false,
      loading: false,
    };
  },

  created() {
    this.updateTranslations();
  },

  watch: {
    "$i18n.locale": function () {
      this.updateTranslations();
      this.$forceUpdate();
    },
    immediate: true,
    deep: true,
  },
  methods: {
    can,
    updateTranslations() {
      this.headers = [
        {
          text: this.$t("newUserTransactions.row"),
          value: "index",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: this.$t("newUserTransactions.uniqueId"),
          value: "uniqueId",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: this.$t("newUserTransactions.amount"),
          value: "amount.$numberDecimal",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: this.$t("newUserTransactions.time"),
          value: "time",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: this.$t("newUserTransactions.createdAt"),
          value: "createdAt",
          align: "center",
          class: "font-weight-bold h6",
        },
        {
          text: this.$t("newUserTransactions.description"),
          value: "description",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: this.$t("newUserTransactions.type"),
          value: "type",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: this.$t("newUserTransactions.product_description"),
          value: "product.description",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: this.$t("newUserTransactions.serviceId"),
          value: "serviceId",
          align: "center",
          class: "font-weight-bold h6",
          sortable: false,
        },
        {
          text: this.$t("newUserTransactions.balanceAfter"),
          value: "balanceAfter.$numberDecimal",
          align: "center",
          class: "font-weight-bold h6",
        },
      ];
    },
    walletShow({ id }) {
      this.userBalances = {
        core: 0,
        payamak: 0,
        statePrice: 0,
      };
      this.priceLoading = true;
      this.walletModal = true;

      const userWallet = () =>
        new Promise((resolve, reject) => {
          this.requestApiGet("newWallet/userWallet", { userId: id })
            .then(({ data }) => {
              let price =
                data?.data &&
                Object.hasOwn(data.data, "balance") &&
                typeof data.data.balance !== "object"
                  ? data.data.balance
                  : data.data.balance.$numberDecimal;
              resolve(price);
            })
            .catch(({ response }) => {
              reject("خطا");
              this.$toast.error(
                response?.data?.message ||
                  this.$t("newUserTransactions.error.terminalWallet")
              );
            });
        });

      const payamakWallet = () =>
        new Promise((resolve, reject) => {
          this.requestApiPostPayamak("wallet/admin/balance", { user_id: id })
            .then(({ data }) => {
              resolve(data.data.balance);
            })
            .catch((error) => {
              reject("خطا");
            });
        });

      const stateWallet = () =>
        new Promise((resolve, reject) => {
          this.requestApiPostState("user/wallet/admin/balance", { user_id: id })
            .then(({ data }) => {
              resolve(data.data.balance);
            })
            .catch((error) => {
              reject("خطا");
            });
        });

      userWallet()
        .then((price) => {
          this.userBalances.core = price;
          payamakWallet().then((price) => {
            this.userBalances.payamak = price;
            stateWallet().then((price) => {
              this.userBalances.statePrice = price;
            });
          });
        })
        .finally(() => (this.priceLoading = false));
    },
    transactionShow(query = this.tableProps) {
      this.loading = true;
      this.transactionListModal = true;

      this.requestApiGet(`newWallet/transactions?userId=${this.data.id}`, query)
        .then(({ data }) => {
          console.log(data.data.data);
          this.transactions = data.data;
        })
        .finally(() => (this.loading = false));
    },
    onPagination(e) {
      let props = {
        ...this.tableProps,
        ...e,
      };
      this.transactionShow(props);
    },
  },
};
</script>

<style scoped>
.link-wrapper >>> a {
  padding: 2px 4px;
  margin: 0 4px;
  background-color: #0d8ddc;
  border-radius: 4px;
  color: white;
  transition: background-color 0.1s ease-out;
}

.link-wrapper >>> a:hover {
  background-color: #2ea0ec;
}
</style>
